import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wand2, AlertTriangle, FileText, Linkedin } from "lucide-react";
import { Candidate } from "@/types/candidate";
import { FieldConfig, FieldType } from "@/types/merge";
import { 
	linkedInCandidateMergeConfig, 
	convertLinkedInToCandidate, 
	LinkedInProfileData 
} from "@/config/linkedInCandidateMergeConfig";
import { MergeUtils } from "@/utils/mergeUtils";
import { AIMergeService } from "@/services/aiMergeService";

interface LinkedInCandidateMergerProps {
	isOpen: boolean;
	onClose: () => void;
	candidate: Candidate;
	linkedInProfile: LinkedInProfileData;
	onSave: (mergedCandidate: Candidate) => Promise<void>;
}

export const LinkedInCandidateMerger = ({
	isOpen,
	onClose,
	candidate,
	linkedInProfile,
	onSave
}: LinkedInCandidateMergerProps) => {
	// Convert LinkedIn profile to candidate format for comparison
	const linkedInCandidate = useMemo(() => 
		convertLinkedInToCandidate(linkedInProfile, candidate), 
		[linkedInProfile, candidate]
	);

	// Create array with original candidate first, then LinkedIn data
	const candidatesForMerge = useMemo(() => [candidate, linkedInCandidate], [candidate, linkedInCandidate]);
	
	// Get field configurations
	const fieldConfigs = linkedInCandidateMergeConfig.fields;

	// State management (similar to DuplicateManager)
	const [selectedByField, setSelectedByField] = useState<Record<string, number>>({});
	const [customValues, setCustomValues] = useState<Record<string, any>>({});
	const [multiSelectedByField, setMultiSelectedByField] = useState<Record<string, Set<number>>>({});
	const [isMerging, setIsMerging] = useState(false);
	const [mergeError, setMergeError] = useState<string>('');
	const [aiMerging, setAiMerging] = useState<Record<string, boolean>>({});
	const [showPreview, setShowPreview] = useState(false);

	// Initialize selections with LinkedIn data preferred for key fields
	useEffect(() => {
		if (!isOpen) return;

		const initialSelections: Record<string, number> = {};
		const initialMultiSelections: Record<string, Set<number>> = {};

		fieldConfigs.forEach(field => {
			// Check if LinkedIn has better data for this field
			const linkedInValue = linkedInCandidate[field.name as keyof Candidate];
			const candidateValue = candidate[field.name as keyof Candidate];
			
			if (field.mergeBehavior === 'combine' && field.type === 'array') {
				// For array fields, start with both selected if both have data
				initialMultiSelections[field.name] = new Set();
				if (candidateValue && Array.isArray(candidateValue) && candidateValue.length > 0) {
					initialMultiSelections[field.name].add(0);
				}
				if (linkedInValue && Array.isArray(linkedInValue) && linkedInValue.length > 0) {
					initialMultiSelections[field.name].add(1);
				}
			} else if (field.mergeBehavior === 'ai') {
				// For AI merge fields, start with both selected for better merge
				initialMultiSelections[field.name] = new Set();
				if (candidateValue && String(candidateValue).trim()) {
					initialMultiSelections[field.name].add(0);
				}
				if (linkedInValue && String(linkedInValue).trim()) {
					initialMultiSelections[field.name].add(1);
				}
			} else {
				// For single-select fields, prefer LinkedIn if it has better data
				if (linkedInValue && (!candidateValue || 
					(typeof linkedInValue === 'string' && linkedInValue.length > (candidateValue as string)?.length))) {
					initialSelections[field.name] = 1; // LinkedIn data
				} else if (candidateValue) {
					initialSelections[field.name] = 0; // Existing candidate data
				} else {
					initialSelections[field.name] = 1; // Default to LinkedIn even if empty
				}
			}
		});

		setSelectedByField(initialSelections);
		setMultiSelectedByField(initialMultiSelections);
	}, [isOpen, candidate, linkedInCandidate, fieldConfigs]);

	// Check if field uses combine mode
	const isFieldCombineMode = useCallback((fieldName: string): boolean => {
		const field = fieldConfigs.find(f => f.name === fieldName);
		return field?.mergeBehavior === 'combine' || field?.mergeBehavior === 'ai';
	}, [fieldConfigs]);

	// Handle multi-selection toggle
	const handleMultiSelectionToggle = useCallback((fieldName: string, index: number) => {
		setMultiSelectedByField(prev => {
			const current = prev[fieldName] || new Set();
			const updated = new Set(current);
			
			if (updated.has(index)) {
				updated.delete(index);
			} else {
				updated.add(index);
			}
			
			return { ...prev, [fieldName]: updated };
		});
	}, []);

	// AI merge functionality
	const handleAIMerge = useCallback(async (fieldName: string) => {
		const field = fieldConfigs.find(f => f.name === fieldName);
		if (!field || !field.aiMergeEnabled) return;

		setAiMerging(prev => ({ ...prev, [fieldName]: true }));

		try {
			// Get values based on current selection logic
			let valuesToMerge: any[] = [];

			const isCombineMode = isFieldCombineMode(fieldName);
			const multiSelected = multiSelectedByField[fieldName] || new Set();
			const hasCustomValue = customValues[fieldName] !== undefined;

			if (isCombineMode && multiSelected.size > 0) {
				// Use values from multi-selected records
				const selectedIndices = Array.from(multiSelected);
				valuesToMerge = selectedIndices
					.map(idx => candidatesForMerge[idx][fieldName as keyof Candidate])
					.filter(v => v !== null && v !== undefined && String(v).trim() !== '');
			} else if (selectedByField[fieldName] !== undefined) {
				// Use value from single selected record
				const selectedValue = candidatesForMerge[selectedByField[fieldName]]?.[fieldName as keyof Candidate];
				if (selectedValue !== null && selectedValue !== undefined && String(selectedValue).trim() !== '') {
					valuesToMerge = [selectedValue];
				}
			}

			// If there's already a custom value, include it for incremental merging
			if (hasCustomValue && customValues[fieldName] && String(customValues[fieldName]).trim() !== '') {
				valuesToMerge.unshift(customValues[fieldName]); // Add current custom value at beginning
			}

			if (valuesToMerge.length === 0) {
				console.warn(`No values selected for AI merge of field: ${fieldName}`);
				return;
			}

			const merged = await AIMergeService.mergeValues(valuesToMerge, 'candidate');
			setCustomValues(prev => ({ ...prev, [fieldName]: merged }));
			
			// Clear field selection when using custom value
			if (!isCombineMode) {
				setSelectedByField(prev => ({ ...prev, [fieldName]: -1 }));
			}
		} catch (error) {
			console.error(`AI merge failed for ${fieldName}:`, error);
		} finally {
			setAiMerging(prev => ({ ...prev, [fieldName]: false }));
		}
	}, [candidatesForMerge, fieldConfigs, isFieldCombineMode, multiSelectedByField, selectedByField, customValues]);

	// Build merged candidate
	const mergedCandidate: Candidate = useMemo(() => {
		const result = { ...candidate } as Record<string, any>; // Start with existing candidate data

		fieldConfigs.forEach(field => {
			const fieldName = field.name;
			
			if (customValues[field.name] !== undefined) {
				result[fieldName] = customValues[field.name];
			} else if (isFieldCombineMode(field.name)) {
				const multiSelected = multiSelectedByField[field.name] || new Set();
				if (multiSelected.size > 0) {
					const selectedValues = Array.from(multiSelected)
						.map(index => candidatesForMerge[index][fieldName as keyof Candidate])
						.filter(value => value !== null && value !== undefined);
					
					if (field.type === 'array') {
						const combined = selectedValues.flat();
						result[fieldName] = [...new Set(combined)];
					} else {
						result[fieldName] = selectedValues[0];
					}
				}
			} else {
				const selectedIndex = selectedByField[field.name];
				if (selectedIndex !== undefined && selectedIndex >= 0 && selectedIndex < candidatesForMerge.length) {
					const selectedValue = candidatesForMerge[selectedIndex][fieldName as keyof Candidate];
					if (selectedValue !== null && selectedValue !== undefined) {
						result[fieldName] = selectedValue;
					}
				}
			}
		});

		// ALWAYS include the full LinkedIn JSON data (not shown in UI but always updated)
		result.linkedin_json = linkedInProfile;

		return result as Candidate;
	}, [candidate, candidatesForMerge, selectedByField, multiSelectedByField, customValues, fieldConfigs, isFieldCombineMode, linkedInProfile]);

	// Handle save
	const handleSave = async () => {
		setIsMerging(true);
		setMergeError('');

		try {
			await onSave(mergedCandidate);
			onClose();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Failed to save candidate';
			setMergeError(errorMessage);
		} finally {
			setIsMerging(false);
		}
	};

	// Get display value for a field
	const getDisplayValue = (item: Candidate, fieldName: string, fieldType: FieldType): string => {
		const value = item[fieldName as keyof Candidate];
		const displayResult = MergeUtils.getDisplayValue(value, fieldType);
		return typeof displayResult === 'object' ? displayResult.value : displayResult;
	};

	// Render field input
	const renderFieldInput = (field: FieldConfig) => {
		const hasCustomValue = customValues[field.name] !== undefined;
		const customValue = hasCustomValue ? customValues[field.name] : '';

		switch (field.type) {
			case 'text':
			case 'email':
			case 'phone':
			case 'url':
				return (
					<div className="space-y-2">
						<Input
							value={typeof customValue === 'string' ? customValue : ''}
							onChange={(e) => setCustomValues(prev => ({ ...prev, [field.name]: e.target.value }))}
							placeholder={`Enter custom ${field.label.toLowerCase()}`}
							className="text-sm"
							type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
						/>
						{field.aiMergeEnabled && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => handleAIMerge(field.name)}
								disabled={aiMerging[field.name]}
								className="w-full text-xs"
							>
								{aiMerging[field.name] ? (
									<><Loader2 className="h-3 w-3 animate-spin mr-1" />AI Merging...</>
								) : (
									<><Wand2 className="h-3 w-3 mr-1" />AI Merge</>
								)}
							</Button>
						)}
					</div>
				);
			
			case 'select':
				return (
					<Select 
						value={customValue || ''} 
						onValueChange={(value) => setCustomValues(prev => ({ ...prev, [field.name]: value }))}
					>
						<SelectTrigger className="text-sm">
							<SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
						</SelectTrigger>
						<SelectContent>
							{field.possibleValues?.map(option => (
								<SelectItem key={option} value={option}>{option}</SelectItem>
							))}
						</SelectContent>
					</Select>
				);

			case 'array':
				return (
					<Textarea
						value={Array.isArray(customValue) ? customValue.join(', ') : ''}
						onChange={(e) => {
							const values = e.target.value.split(',').map(v => v.trim()).filter(v => v);
							setCustomValues(prev => ({ ...prev, [field.name]: values }));
						}}
						placeholder={`Enter ${field.label.toLowerCase()}, comma separated`}
						className="text-sm min-h-16"
					/>
				);

			default:
				return (
					<Input
						value={String(customValue)}
						onChange={(e) => setCustomValues(prev => ({ ...prev, [field.name]: e.target.value }))}
						placeholder={`Enter custom ${field.label.toLowerCase()}`}
						className="text-sm"
					/>
				);
		}
	};

	if (!isOpen) return null;

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-6xl max-h-[95vh] w-[95vw] flex flex-col">
				<DialogHeader className="flex-shrink-0">
					<DialogTitle className="flex items-center gap-2">
						<Linkedin className="h-5 w-5 text-blue-500" />
						Update Candidate with LinkedIn Data
					</DialogTitle>
					<div className="text-sm text-gray-600 space-y-1">
						<p>Compare and merge your candidate data with LinkedIn profile information.</p>
						<div className="flex gap-4 text-xs">
							<span>✓ = Single selection</span>
							<span>● = Multi-selection (combine mode)</span>
						</div>
					</div>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto min-h-0">
					<div className="overflow-x-auto border rounded-sm">
						<table className="min-w-full text-sm">
							<thead>
								<tr>
									<th className="border px-2 py-1 text-left bg-gray-50 w-48">Field</th>
									<th className="border px-2 py-1 bg-blue-50 min-w-40">
										<div className="flex items-center gap-2">
											<span>Current Candidate</span>
											<Badge variant="outline" className="text-xs">Original</Badge>
										</div>
									</th>
									<th className="border px-2 py-1 bg-blue-100 min-w-40">
										<div className="flex items-center gap-2">
											<Linkedin className="h-4 w-4 text-blue-500" />
											<span>LinkedIn Profile</span>
											<Badge variant="default" className="text-xs bg-blue-500">New</Badge>
										</div>
									</th>
									<th className="border px-2 py-1 bg-gray-50 min-w-60">Merged Value</th>
								</tr>
							</thead>
							<tbody>
								{fieldConfigs.map(field => {
									const hasCustomValue = customValues[field.name] !== undefined;
									const isCombineMode = isFieldCombineMode(field.name);
									const multiSelected = multiSelectedByField[field.name] || new Set();

									return (
										<tr key={field.name}>
											<td className="border px-2 py-1 font-medium bg-gray-50 align-top">
												<div className="space-y-1">
													<div className="font-medium flex items-center gap-2">
														{field.label}
														{isCombineMode && (
															<Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
																Multi-select
															</Badge>
														)}
													</div>
													{field.description && (
														<div className="text-xs text-gray-500">{field.description}</div>
													)}
												</div>
											</td>
											
											{/* Current candidate data */}
											<td
												className={`border px-2 py-1 cursor-pointer transition-colors align-top ${
													isCombineMode
														? (multiSelected.has(0) ? 'bg-green-100 border-green-500' : 'hover:bg-gray-100')
														: (selectedByField[field.name] === 0 && !hasCustomValue ? 'bg-green-100 border-green-500' : 'hover:bg-gray-100')
												}`}
												onClick={() => {
													if (isCombineMode) {
														handleMultiSelectionToggle(field.name, 0);
													} else {
														setSelectedByField(prev => ({ ...prev, [field.name]: 0 }));
														if (hasCustomValue) {
															setCustomValues(prev => {
																const { [field.name]: _, ...rest } = prev;
																return rest;
															});
														}
													}
												}}
											>
												<div className="min-h-8 break-words">
													{field.type === 'array' && Array.isArray(candidate[field.name as keyof Candidate]) ? (
														<div className="font-mono text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap max-h-20 overflow-y-auto">
															{(candidate[field.name as keyof Candidate] as string[]).join('\n')}
														</div>
													) : (
														getDisplayValue(candidate, field.name, field.type) || (
															<span className="text-gray-400 italic">No data</span>
														)
													)}
													{/* Selection indicator */}
													{(isCombineMode ? multiSelected.has(0) : (selectedByField[field.name] === 0 && !hasCustomValue)) && (
														<span className="ml-1 text-green-600 font-bold">
															{isCombineMode ? '●' : '✓'}
														</span>
													)}
												</div>
											</td>

											{/* LinkedIn data */}
											<td
												className={`border px-2 py-1 cursor-pointer transition-colors align-top ${
													isCombineMode
														? (multiSelected.has(1) ? 'bg-green-100 border-green-500' : 'hover:bg-gray-100')
														: (selectedByField[field.name] === 1 && !hasCustomValue ? 'bg-green-100 border-green-500' : 'hover:bg-gray-100')
												}`}
												onClick={() => {
													if (isCombineMode) {
														handleMultiSelectionToggle(field.name, 1);
													} else {
														setSelectedByField(prev => ({ ...prev, [field.name]: 1 }));
														if (hasCustomValue) {
															setCustomValues(prev => {
																const { [field.name]: _, ...rest } = prev;
																return rest;
															});
														}
													}
												}}
											>
												<div className="min-h-8 break-words">
													{field.type === 'array' && Array.isArray(linkedInCandidate[field.name as keyof Candidate]) ? (
														<div className="font-mono text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap max-h-20 overflow-y-auto">
															{(linkedInCandidate[field.name as keyof Candidate] as string[]).join('\n')}
														</div>
													) : (
														getDisplayValue(linkedInCandidate, field.name, field.type) || (
															<span className="text-gray-400 italic">No data</span>
														)
													)}
													{/* Selection indicator */}
													{(isCombineMode ? multiSelected.has(1) : (selectedByField[field.name] === 1 && !hasCustomValue)) && (
														<span className="ml-1 text-green-600 font-bold">
															{isCombineMode ? '●' : '✓'}
														</span>
													)}
												</div>
											</td>

											{/* Custom input */}
											<td className="border px-2 py-1 align-top">
												{renderFieldInput(field)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>

				<DialogFooter className="flex-shrink-0 flex justify-between">
					<Button variant="outline" onClick={onClose}>Cancel</Button>
					
					<div className="flex gap-2">
						{mergeError && (
							<div className="flex-1 px-4">
								<div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
									<strong>Error:</strong> {mergeError}
								</div>
							</div>
						)}

						<Button
							onClick={handleSave}
							disabled={isMerging}
							className="bg-blue-500 hover:bg-blue-500 text-white disabled:opacity-50"
						>
							{isMerging ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin mr-2" />
									Updating Candidate...
								</>
							) : (
								<>
									<Linkedin className="h-4 w-4 mr-2" />
									Update Candidate
								</>
							)}
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};